import {
  BOGGLE_GOLD_TILE_KIND,
  BOGGLE_SPECIAL_TILE_KINDS,
  type BoggleSpecialTileKind,
} from '~/boggle/config/specialTiles/kinds'
import { BOGGLE_SPECIAL_TILE_REGISTRY } from '~/boggle/config/specialTiles/registry'
import type { BogglePosition } from '~/boggle/engine/standard/types'
import { isBoggleSpecialTile } from './index'

export type BoggleAcceptedWordPathStep = {
  effectiveSpecialKinds: readonly BoggleSpecialTileKind[]
  letter: string
  physicalSpecialKinds: readonly BoggleSpecialTileKind[]
  position: BogglePosition
  virtualSpecialKinds: readonly BoggleSpecialTileKind[]
}

/** Older persisted facts may still carry gold-source and `specialKinds` field names. */
export type BoggleLegacyAcceptedWordPathStep = {
  effectiveGoldSource?: 'gold-rush' | 'tile' | null
  effectiveSpecialKinds?: readonly BoggleSpecialTileKind[]
  letter: string
  physicalSpecialKinds?: readonly BoggleSpecialTileKind[]
  position: BogglePosition
  specialKinds?: readonly BoggleSpecialTileKind[]
  virtualSpecialKinds?: readonly BoggleSpecialTileKind[]
}

export type BoggleSpecialTileCountRecord = Record<BoggleSpecialTileKind, number>

export function createEmptySpecialTileCounts(): BoggleSpecialTileCountRecord {
  return Object.fromEntries(
    BOGGLE_SPECIAL_TILE_KINDS.map(kind => [kind, 0]),
  ) as BoggleSpecialTileCountRecord
}

export function normalizeLegacyPathStep(
  step: BoggleLegacyAcceptedWordPathStep,
): BoggleAcceptedWordPathStep {
  if (step.physicalSpecialKinds !== undefined && step.virtualSpecialKinds !== undefined) {
    return {
      effectiveSpecialKinds: [...(step.effectiveSpecialKinds ?? [])],
      letter: step.letter,
      physicalSpecialKinds: [...step.physicalSpecialKinds],
      position: { ...step.position },
      virtualSpecialKinds: [...step.virtualSpecialKinds],
    }
  }

  const physicalSpecialKinds: readonly BoggleSpecialTileKind[] = [
    ...(step.specialKinds ?? step.physicalSpecialKinds ?? []),
  ]
  const virtualSpecialKinds: readonly BoggleSpecialTileKind[] =
    step.virtualSpecialKinds !== undefined
      ? [...step.virtualSpecialKinds]
      : step.effectiveGoldSource === 'gold-rush' &&
          !physicalSpecialKinds.includes(BOGGLE_GOLD_TILE_KIND)
        ? [BOGGLE_GOLD_TILE_KIND]
        : []

  const effectiveSpecialKinds: readonly BoggleSpecialTileKind[] =
    step.effectiveSpecialKinds !== undefined
      ? [...step.effectiveSpecialKinds]
      : [...new Set([...physicalSpecialKinds, ...virtualSpecialKinds])]

  return {
    effectiveSpecialKinds,
    letter: step.letter,
    physicalSpecialKinds,
    position: { ...step.position },
    virtualSpecialKinds,
  }
}

export function buildPhysicalSpecialTileCounts(
  pathSteps: readonly BoggleLegacyAcceptedWordPathStep[],
): BoggleSpecialTileCountRecord {
  const counts = createEmptySpecialTileCounts()
  for (const step of pathSteps) {
    const normalized = normalizeLegacyPathStep(step)
    for (const kind of normalized.physicalSpecialKinds) {
      counts[kind] += 1
    }
  }
  return counts
}

export function buildEffectiveSpecialTileCounts(
  pathSteps: readonly BoggleLegacyAcceptedWordPathStep[],
): BoggleSpecialTileCountRecord {
  const counts = createEmptySpecialTileCounts()
  for (const step of pathSteps) {
    const normalized = normalizeLegacyPathStep(step)
    for (const kind of normalized.effectiveSpecialKinds) {
      counts[kind] += 1
    }
  }
  return counts
}

/** Virtual gold > physical gold > other physical kinds (registry order). */
export function resolvePathStepDisplayKind(
  step: BoggleLegacyAcceptedWordPathStep,
): BoggleSpecialTileKind | null {
  const normalized = normalizeLegacyPathStep(step)
  if (normalized.virtualSpecialKinds.includes(BOGGLE_GOLD_TILE_KIND)) {
    return BOGGLE_GOLD_TILE_KIND
  }
  if (normalized.physicalSpecialKinds.includes(BOGGLE_GOLD_TILE_KIND)) {
    return BOGGLE_GOLD_TILE_KIND
  }
  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    if (normalized.physicalSpecialKinds.includes(kind)) {
      return kind
    }
  }
  return null
}

export function resolveLiveCellDisplayKind(
  cell: { tags?: readonly string[] } | null | undefined,
  goldRushActive: boolean,
): BoggleSpecialTileKind | null {
  if (!cell) {
    return null
  }
  if (goldRushActive) {
    return BOGGLE_GOLD_TILE_KIND
  }
  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    if (isBoggleSpecialTile(cell, kind)) {
      return kind
    }
  }
  return null
}

export function getSpecialTilePreviewFill(kind: BoggleSpecialTileKind): string {
  return BOGGLE_SPECIAL_TILE_REGISTRY[kind].previewFill
}

export function getSpecialTilePreviewTextColor(kind: BoggleSpecialTileKind): string {
  return BOGGLE_SPECIAL_TILE_REGISTRY[kind].previewTextColor
}

export function getSpecialTilePreviewTextShadow(kind: BoggleSpecialTileKind): string {
  const fill = getSpecialTilePreviewFill(kind)
  return `1px 0 ${fill}, -1px 0 ${fill}, 0 1px ${fill}, 0 -1px ${fill}, 0 0 2px ${fill}`
}

export function hexColorToNumber(hex: string): number {
  return Number.parseInt(hex.replace('#', ''), 16)
}

export function getSpecialTilePreviewFillNumber(kind: BoggleSpecialTileKind): number {
  return hexColorToNumber(getSpecialTilePreviewFill(kind))
}

export function getSpecialTilePreviewTextColorNumber(kind: BoggleSpecialTileKind): number {
  return hexColorToNumber(getSpecialTilePreviewTextColor(kind))
}

export function isEffectiveGoldPathStep(step: BoggleLegacyAcceptedWordPathStep): boolean {
  const normalized = normalizeLegacyPathStep(step)
  return normalized.effectiveSpecialKinds.includes(BOGGLE_GOLD_TILE_KIND)
}

export function getEffectiveGoldCount(counts: BoggleSpecialTileCountRecord): number {
  return counts[BOGGLE_GOLD_TILE_KIND]
}

export function getVirtualGoldCount(
  effectiveCounts: BoggleSpecialTileCountRecord,
  physicalCounts: BoggleSpecialTileCountRecord,
): number {
  return Math.max(
    0,
    getEffectiveGoldCount(effectiveCounts) - getEffectiveGoldCount(physicalCounts),
  )
}
